import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ShieldCheck, Loader2, Building2, Calendar, FileText, User } from 'lucide-react';
import api from '@/lib/api';

const LOGO = 'https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/bwe3ss1d_1.png';
const ACCENT = '#00875A';

export default function VerifyCertificate() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/verify/${token}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ valid: false, reason: 'Error de red al verificar' }))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
            <Loader2 className="w-10 h-10 animate-spin" style={{ color: ACCENT }} />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white">
            <div className="max-w-3xl mx-auto p-6 md:p-12">
                <div className="flex items-center gap-4 mb-10">
                    <img src={LOGO} alt="AIGIS" className="h-14 w-14 object-contain" />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">AIGIS CERTIFICATE</div>
                        <div className="font-display font-black text-2xl">Verificación pública</div>
                    </div>
                </div>

                {data?.valid ? (
                    <ValidCertificate data={data} />
                ) : (
                    <InvalidCertificate reason={data?.reason || 'Distintivo no válido'} />
                )}

                <div className="mt-12 pt-6 border-t border-white/10 text-xs opacity-60 flex items-center justify-between flex-wrap gap-2">
                    <span>Todos los distintivos AIGIS incluyen firma criptográfica HMAC-SHA256. Cualquier alteración invalida el documento.</span>
                    <Link to="/" className="underline" style={{ color: ACCENT }}>Volver al inicio</Link>
                </div>
            </div>
        </div>
    );
}

function ValidCertificate({ data }) {
    return (
        <div className="border-2 p-6 md:p-8" style={{ borderColor: ACCENT, background: 'rgba(0,135,90,0.06)' }}
            data-testid="verify-valid">
      {data.estado_distintivo === 'observacion' && (
        <div className="mb-4 p-3 text-sm font-bold" style={{ background: '#FEF3C7', color: '#92400E' }}
          data-testid="verify-observacion">
          Distintivo en observación: tiene hasta la siguiente auditoría para corregir.
        </div>
      )}
            <div className="flex items-center gap-3 mb-5">
                <div className="w-14 h-14 flex items-center justify-center" style={{ background: ACCENT }}>
                    <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: ACCENT }}>Distintivo vigente</div>
                    <div className="font-display font-black text-3xl">Otorgado por AIGIS Certificate</div>
                </div>
            </div>

            <div className="mb-5 p-3 text-xs" style={{ background: 'rgba(255,255,255,0.04)', borderLeft: `3px solid ${ACCENT}` }}>
                Distintivo propio de AIGIS Certificate, otorgado con base en auditoría interna realizada por
                nuestro equipo de auditores. No sustituye acreditaciones oficiales de organismos gubernamentales.
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <Field Icon={FileText} label="Distintivo" value={data.distintivo_names?.length > 0 ? data.distintivo_names.join(' + ') : 'Distintivo AIGIS Certificate'} />
                <Field Icon={Building2} label="Cliente" value={data.client.trade_name ? `${data.client.name} · ${data.client.trade_name}` : data.client.name} />
                <Field Icon={ShieldCheck} label="RFC" value={data.client.rfc || '—'} />
                <Field Icon={Building2} label="Industria" value={data.client.industry || '—'} />
                <Field Icon={User} label="Auditor" value={data.inspector?.name || '—'} />
                <Field Icon={Calendar} label="Emitido" value={data.certificate.issued_at?.slice(0, 10)}
                    sub={data.certificate.score != null ? `Score: ${data.certificate.score.toFixed(2)}%` : ''} />
            </div>

            <div className="mt-6 p-3 border font-mono text-[10px] break-all opacity-70" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
                Distintivo #{data.certificate.id}
            </div>
        </div>
    );
}

function InvalidCertificate({ reason }) {
    return (
        <div className="border-2 p-6 md:p-8" style={{ borderColor: '#EF4444', background: 'rgba(239,68,68,0.06)' }}
            data-testid="verify-invalid">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 flex items-center justify-center bg-[#EF4444]">
                    <XCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#EF4444]">Distintivo INVÁLIDO</div>
                    <div className="font-display font-black text-3xl">No es auténtico</div>
                </div>
            </div>
            <div className="opacity-80">
                {reason}
            </div>
            <div className="mt-4 text-sm opacity-70">
                Si crees que esto es un error, comunícate con soporte de AIGIS Certificate para validar.
            </div>
        </div>
    );
}

function Field({ Icon, label, value, sub }) {
    return (
        <div className="p-3 border" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="font-display font-black text-lg">{value || '—'}</div>
            {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
        </div>
    );
}
